import http from "../../utils/http-common";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export function useRegions() {
  return useQuery({
    queryKey: ["regions"],
    queryFn: async () => {
      const { data } = await http.get("/instances/regions?type=Google");
      return data.objects;
    },
  });
}

export function useZones(region: string | null) {
  return useQuery({
    queryKey: ["zones", region],
    queryFn: async () => {
      if (!region) return [];
      const { data } = await http.get(`/instances/${region}/zones?type=Google`);
      return data.objects;
    },
    enabled: !!region,
  });
}
export function useInstanceByName(name?: string, zone: string = "us-east1-d") {
  return useQuery({
    queryKey: ["instance", name, zone],
    queryFn: async () => {
      if (!name) return [];
      const { data } = await http.get(`/instances/${name}?type=Google&zone=${zone}`);
      return data.objects;
    },
    enabled: !!name,
  });
}
export function useInstances(region?: string, zone?: string) {
  return useQuery({
    queryKey: ["instances", region, zone],
    queryFn: async () => {
      let query = `/instances?type=Google`;
      if (region) query += `&region=${region}`;
      if (zone) query += `&zone=${zone}`;
      const { data } = await http.get(query);
      return data.objects;
    },
    enabled: !!region,
  });
}

export function useCreateInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      name, machine, image, zone, region, os,
    }: {
      name: string;
      machine: string;
      image: string;
      zone: string;
      region: string;
      os?: string;
    }) => {
      const params = [
        `type=Google`,
        `name=${encodeURIComponent(name)}`,
        `machine=${encodeURIComponent(machine)}`,
        `image=${encodeURIComponent(image)}`,
        `zone=${zone}`,
        `region=${region}`,
      ];
      if (os) params.push(`os=${encodeURIComponent(os)}`);
      const { data } = await http.post(`/instances?${params.join("&")}`);
      return data.objects;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["instances", variables.region, variables.zone] });
    },
  });
}

export function useDeleteInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, zone, region }: { name: string; zone: string; region: string }) => {
      const { data } = await http.delete(
        `/instances/${name}?type=Google&zone=${zone}&region=${region}`
      );
      return data.objects;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["instances", variables.region, variables.zone] });
    },
  });
}

export function useStopInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, zone, region }: { name: string; zone: string; region: string }) => {
      const { data } = await http.post(
        `/instances/${name}/stop?type=Google&zone=${zone}&region=${region}`
      );
      return data.objects;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["instances", variables.region, variables.zone] });
    },
  });
}

export function useSuspendInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, zone, region }: { name: string; zone: string; region: string }) => {
      const { data } = await http.post(
        `/instances/${name}/suspend?type=Google&zone=${zone}&region=${region}`
      );
      return data.objects;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["instances", variables.region, variables.zone] });
    },
  });
}

export function useMachineTypes(zone: string) {
  return useQuery({
    queryKey: ["machine-types", zone],
    queryFn: async () => {
      if (!zone) return [];
      const { data } = await http.get(`/instances/machine-types?type=Google&zone=${zone}`);
      return data.objects;
    },
    enabled: !!zone,
  });
}

export function useImages(os?: string) {
  return useQuery({
    queryKey: ["images", os],
    queryFn: async () => {
      const { data } = await http.get(`/images?os=${os || ""}`);
      return data.objects;
    },
    enabled: !!os,
  });
}

export function useStartInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, zone, region }: { name: string; zone: string; region: string }) => {
      const { data } = await http.post(
        `/instances/${name}/start?type=Google&zone=${zone}&region=${region}`
      );
      return data.objects;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["instances", variables.region, variables.zone] });
    },
  });
}

export function useResumeInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, zone, region }: { name: string; zone: string; region: string }) => {
      const { data } = await http.post(
        `/instances/${name}/resume?type=Google&zone=${zone}&region=${region}`
      );
      return data.objects;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["instances", variables.region, variables.zone] });
    },
  });
}